import { useState } from 'react'

import styled from 'styled-components'

import {
  isAdmin,
  isBDM,
  isCasualInspector,
  isFinance,
  isLeasingConsultant,
  isPropertyManager,
  isTeamLeader,
} from 'app/bond_cover/agency/constants'
import { Box, Button, Modal } from 'app/components/design-system-components'
import { Text } from 'app/components/design-system-components/typography'
import { CheckBoxGeneral } from 'app/components/forms/forms'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { TEAM_ROLE_LEARN_MORE_LINK } from 'app/sm/team_viewing_preference/setup/helpers'

type ChangeRoleModalProps = {
  toggleModal: () => void
  name: string
  onClickUpdateTeamMember: (roles: {
    isAdmin: boolean
    isTeamLeader: boolean
    isLeasingConsultant: boolean
    isPropertyManager: boolean
    isCasualInspector: boolean
    isBDM: boolean
    isFinance: boolean
  }) => void
  initialRoles: {
    isAdmin: boolean
    isTeamLeader: boolean
    isLeasingConsultant: boolean
    isPropertyManager: boolean
    isCasualInspector: boolean
    isBDM: boolean
    isFinance: boolean
  }
  email: string
}

const StyledLink = styled(Button)`
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
`

function ChangeRoleModal({
  toggleModal,
  name,
  onClickUpdateTeamMember,
  email,
  initialRoles,
}: ChangeRoleModalProps) {
  const displayName = name.trim().length > 0 ? name : email

  const [roles, setRoles] = useState(initialRoles)

  // Check if at least one role is selected
  const hasAtLeastOneRole = Object.values(roles).some((value) => value === true)

  const onLearnMoreClick = () => {
    window.open(TEAM_ROLE_LEARN_MORE_LINK, '_blank')
  }
  const LearnMoreRender = (
    <StyledLink
      ml={theme.space[1] + 'px'}
      variant="linkBlue"
      onClick={onLearnMoreClick}
    >
      learn more
    </StyledLink>
  )

  const handleRoleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    name: string,
  ) => {
    const { checked } = event.target as HTMLInputElement
    setRoles((prev) => ({
      ...prev,
      [name]: checked,
    }))
  }

  const roleOptions = [
    {
      id: 'team-admin',
      label: 'Team Admin (account owner/full access)',
      roleKey: isAdmin,
      checked: roles.isAdmin,
    },
    {
      id: 'team-leader',
      label: 'Team Leader',
      roleKey: isTeamLeader,
      checked: roles.isTeamLeader,
    },
    {
      id: 'leasing-consultant',
      label: 'Leasing Consultant',
      roleKey: isLeasingConsultant,
      checked: roles.isLeasingConsultant,
    },
    {
      id: 'property-manager',
      label: 'Property Manager',
      roleKey: isPropertyManager,
      checked: roles.isPropertyManager,
    },
    {
      id: 'casual-inspector',
      label: 'Casual Inspector (only enquiries and viewings)',
      roleKey: isCasualInspector,
      checked: roles.isCasualInspector,
    },
    { id: 'bdm', label: 'BDM', roleKey: isBDM, checked: roles.isBDM },
    {
      id: 'finance',
      label: 'Finance',
      roleKey: isFinance,
      checked: roles.isFinance,
    },
  ]

  const modalBody = (
    <Box>
      <Text mb={5}>
        Choose the roles that {displayName} will have. {LearnMoreRender}
      </Text>
      {roleOptions.map(({ id, label, roleKey, checked }) => (
        <CheckBoxGeneral
          key={id}
          id={id}
          label={label}
          labelClass="fs16"
          checked={checked}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleRoleChange(event, roleKey)
          }
          eventProcessedByComponent
        />
      ))}
      {!hasAtLeastOneRole && (
        <Box mt={3} fontSize="14px" p="12px" style={{ color: 'red' }}>
          Please select at least one role
        </Box>
      )}
    </Box>
  )
  return (
    <Modal
      modalHeading="Change role"
      modalBody={modalBody}
      toggleModal={toggleModal}
      primaryLabel="Save"
      secondaryLabel="Close"
      primaryAction={() => {
        onClickUpdateTeamMember(roles)
      }}
      secondaryAction={toggleModal}
      modalFooter={false}
      primaryButtonDisabled={!hasAtLeastOneRole}
    />
  )
}

export default ChangeRoleModal
